import { useEffect, useRef, useState, useCallback, useMemo, Fragment, type FormEvent, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { api } from '../../shell/bridge';
import { listDecisions, createDecision, type DecisionView } from '../../lib/api';
import { parseDecisionIntent } from '../../../../shared/decisions';
import type { ThreadMessage, LocalEcho, QueuedSend } from '../../hooks/useThread';
import type { Role } from '../../lib/api';
import { SkeletonBlock } from '../../ui/Skeleton';
import { Overlay } from '../../ui/Overlay';
import { tabListKeyDown } from '../../ui/tabs';
import { ComposeMenu, TILE_DEFS, type ComposeTile } from '../../components/ComposeMenu';
import { Composer } from '../../components/Composer';
import { LifeRail } from '../../components/LifeRail';
/* The cadence-nudge payload is written by the Durable Object and read here —
   one parser in shared/ so the two can never drift (card yejoF8uTVxyf). Same
   precedent as LifeRail's shared/life-rail.ts. Pulling the shared graph into
   this bundle is why web/tsconfig.json carries allowImportingTsExtensions —
   the shared modules import each other with the .ts extension, the convention
   the root tsconfig already allows. */
import { searchExcerpt } from '../../lib/searchExcerpt';
import { parseDateNudge } from '../../../../shared/date-nudge';
/* Card tPPB3ozS4YrK — the hint's lifetime, read from the same constant the
   room's comments quote, so "4s of silence" means one number everywhere. */
import { TYPING_TTL_MS } from '../../../../shared/types';
import { TabHint } from '../../components/TabHint';
import { markHintSeen } from '../../lib/hints';
import { Tooltip } from '../../ui/Tooltip';
/* Jonathan 2026-10-30 (card ZXJan7hSZq2G): confetti on the reveal, only once
   both answers are in. Shared once-per-question guard with the Us > Questions
   hero, so the same reveal never bursts twice. */
/* H7 (card ESidDNa3mmaP): every catch block below keeps its console.error
   for the log and ALSO announces itself through this channel, so a failed
   action never looks like a success. */
import { reportActionFailure } from '../../lib/actionFeedback';
/* Card psHEfEkK9cT2: one confetti contract app-wide — the burst is asked for
   by moment (a kudos seq, a game round), never painted by this screen. */
import { celebrateOnce } from '../../lib/celebrate';
import { ActionErrorToast } from '../../ui/ActionErrorToast';
/* UX item 5 (H5/H6): the composer draft, the filter chip and the stream's
   scroll offset are component state, and this screen unmounts on every tab
   switch — so they are kept in the session bag and restored on the way back. */
import { useRemembered, useScrollMemory } from '../../hooks/useUiMemory.ts';
import type { Dest } from '../../App';
import { useKeyboardInset } from '../../hooks/useKeyboardInset';
import '../../styles/thread.css';
/* Desktop uplift A1b (card sUIMDZCfzM0l) — the right context rail reuses the
   notebook .nb-context primitive (composition, no new design). */
import '../../styles/thread-rail.css';
import { KUDOS_GLYPH, GAME_GLYPH } from './glyphs';
import { dayKey, dayLabel } from './helpers';
import { Bubble, MessageSave } from './message';
import { RequestCard, parseRequestCard, DecisionCard, DecisionDetail, QuestionCard, parseQuestionCard, DateCard, DateNudgeCard } from './cards';

/** Mirrors MAX_THREAD_TEXT in shared/security.ts — the web bundle types its
 *  own surface rather than importing shared/ (same convention as the other
 *  mirrored limits). The server rejects anything longer with 'too_long'; this
 *  stops it before it is sent (card YYrJc5OkW7EP). scripts/probe-security.mjs
 *  asserts both halves agree. */
const THREAD_TEXT_MAX = 4000;
/* ---------------------------------------------------------------------------
   Props — optional onComposePick (App wires it; no-op default so Thread
   compiles standalone without App.tsx changes).
   --------------------------------------------------------------------------- */

interface ThreadProps {
  messages: ThreadMessage[];
  echoes: LocalEcho[];
  /** Sends the socket could not take, in send order (card 4FFux4skKvYw).
   *  Each renders as a pending row IN stream position — the durable record of
   *  words that were kept — while the toast above the composer is the moment.
   *  Optional so the standalone probe mount keeps compiling. */
  queued?: QueuedSend[];
  /** Retry one queued send (row button or toast). Offline is a no-op by
   *  design: the row stays queued and nothing more is said. */
  onRetryQueued?: (clientMsgId: string) => void;
  roleFor: (memberId: string | null | undefined) => Role | null;
  /** The sender's display name (card yFLCjm7ONIEc) — the partner's messages
   *  are labelled with it, so a name change shows on the next message. */
  nameFor?: (memberId: string | null | undefined) => string | null;
  /** The partner's chat name right now, for copy that has to name them before
   *  any of their messages are on screen — the kudos sender reads "For Jane"
   *  rather than "For your partner" (founder email 2026-10-28). Falls back to
   *  the generic phrase when the couple has not named themselves yet. */
  partnerName?: string | null;
  /** The sender's picked reply-text colour (card 9QADJv3CdiOh), already
   *  resolved to a hex by the caller. Null (or absent) = no pick / unknown
   *  key — the bubble keeps its role-token ink. Only the partner's side ever
   *  receives a colour: your own words stay on the cream bubble. */
  colourFor?: (memberId: string | null | undefined) => string | null;
  myMemberId: string | null;
  /** Card tPPB3ozS4YrK — a keystroke signal, already throttled to one frame
   *  per 2s by useThread. Optional so the standalone probe mount, which has no
   *  socket behind it, keeps compiling untouched. */
  onTyping?: () => void;
  /** PEAR_TYPING as /api/auth/config reported it. `=== true` is the only value
   *  that sends a frame OR renders the indicator — the client half of a flag
   *  whose server half stops relaying the moment it is off. */
  typingOn?: boolean;
  /** Second arg = the composer chip was armed (invoke path, card hphiEFLMogbg).
   *  Returns false only when the message was REJECTED outright. A send the
   *  socket could not take returns true because it is queued, not lost — the
   *  queued row is the record and the composer stays empty (card
   *  4FFux4skKvYw). */
  onSend: (text: string, wantsAgent?: boolean) => boolean;
  /** Delete your own message (card — msg-remove): own-text trash affordance
   *  + native long-press on touch. The DO refuses anything not self-authored,
   *  so a stale seq here is a quiet no-op. Optional. */
  onRemoveMessage?: (seq: number) => void;
  /** Skeleton while the first history page is in flight. */
  loading: boolean;
  /** Called when a compose-tile is picked from the + menu. Optional. */
  onComposePick?: (tile: ComposeTile) => void;
  /** Compose menu deep-link (design v1.1 §5.2): opens the kudos sender. */
  composeIntent?: { kind: 'kudos' } | null;
  /** One-shot intent (card 4vIN4UouHPz4): App clears composeIntent the moment
   *  a panel has acted on it. Without this the intent survives every unmount,
   *  so Thread's mount effect re-fires on each return and resurrects the
   *  kudos sender on whatever screen the user is now on. */
  onIntentConsumed?: () => void;
  /** Explicit navigation (card Mn55eOvFgvC0, H4): every thread card that
   *  links to another screen calls this with its own destination — section,
   *  subsection, optional entity — instead of a generic "go to Us". */
  onNavigate?: (dest: Dest) => void;
  /** Game-round card "Open in Us" (card Bkt2clm8SSOI): names the round so the
   *  Us > Game destination opens THAT round (H4). */
  onOpenGameRound?: (roundId: string) => void;
  /** Quick link from the More menu (card yOcdjnR6pXBz): land on Thread with
   *  this filter already selected. null = no request, keep current. */
  requestedFilter?: 'all' | 'chat' | null;
  /** Two members have joined — the invite already landed. Drives the empty
   *  state: a paired couple hears "say hello", a solo creator still gets
   *  the point-back-to-the-pairing-screen copy. Defaults to false so the
   *  probe and standalone use keep the invite wording. */
  paired?: boolean;
  /** Windowed hydration (card 4ynbWszGdqUd): true when the server holds
   *  messages older than the loaded window — shows the expand affordance. */
  hasOlder?: boolean;
  /** Card psHEfEkK9cT2: the history page is in hand. The kudos baseline is
   *  only taken once this is true — `loading` is false on a cold boot while
   *  the token is still null, so it cannot carry this meaning. */
  historyLoaded?: boolean;
  /** Fetch the next-older page (no window). Wired to the expand affordance. */
  onLoadOlder?: () => void;
  /** Archive search (card 2sjo3uNvaRDr): server-side substring match over the
   *  couple's whole thread. Resolves null on failure so the panel can say so
   *  instead of showing an empty list that reads as "no results". */
  onSearch?: (query: string) => Promise<ThreadMessage[] | null>;
  /** Page history so `seq` is loaded (card 2sjo3uNvaRDr). False when the
   *  socket could not take it — the result list stays up rather than closing
   *  on a jump that never happened. */
  onJumpToSeq?: (seq: number) => boolean;
}

/**
 * The couple thread. Renders messages declaratively — React escapes text, so
 * the `escapeHtml` + `innerHTML` pair from main.ts is gone entirely.
 *
 * Home-stream variant (card B):
 *   - Pinned "Tonight" card at top
 *   - TOP-DOCKED composer with ComposeMenu (dZjN98QXAgEH) — respond first
 *   - Filter chips row (All / Chat / Asks / Questions)
 *   - Invite-flavoured empty state
 *
 * Comprehension order is respond > read (card dZjN98QXAgEH, Jonathan
 * 2026-10-12): the composer sits directly under the welcome prompt and the
 * message stream renders BELOW it. The old bottom dock is gone.
 */
export function Thread({ messages, echoes, queued = [], onRetryQueued, roleFor, nameFor, partnerName, colourFor, myMemberId, onTyping, typingOn = false, onSend, onRemoveMessage, loading, historyLoaded = false, onComposePick, composeIntent, onIntentConsumed, onNavigate, onOpenGameRound, requestedFilter, paired = false, hasOlder = false, onLoadOlder, onSearch, onJumpToSeq }: ThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  /* Card NyA5qLuZsAgi (reopened 2026-11-04): the paging control must not be
     visible until the reader reaches the end of the loaded range. It stays IN
     FLOW at that boundary — never sticky or fixed — and is revealed when the
     scrollport is at, or within a short reach of, its oldest end, so the
     discoverability cue is the scroll position itself. A loaded range short
     enough to fit on screen has already reached its end, so it shows at once. */
  const [olderRevealed, setOlderRevealed] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasOlder) {
      setOlderRevealed(false);
      return;
    }
    const REACH = 120;
    const measure = () => {
      const rest = el.scrollHeight - el.scrollTop - el.clientHeight;
      setOlderRevealed(el.scrollHeight <= el.clientHeight + 8 || rest <= REACH);
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      ro?.disconnect();
    };
  }, [hasOlder, messages.length]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /* A half-written message survives leaving the tab (UX item 5). It is cleared
     in the same places it always was — on send, and on a send the socket could
     not take. */
  const [draft, setDraft] = useRemembered('thread.draft', '');
  /* Restores where you were reading. A reader parked at the bottom is left to
     the auto-scroll below (new messages still pull the stream down). */
  useScrollMemory(scrollRef, 'thread');

  /* Grow the box with the draft is the shared Composer's half now (card
     4DrBwftybJ7q) — the Thread passes its ref and the CSS keeps the floors:
     min-height 72px and the 40vh cap. */

  /* ── Kudos (card V0dtVf36Dplz) ─────────────────────────────────────
     composeIntent opens a small "say that was lovely" sender; POSTs to
     /api/chat/kudos, whose appendMessage broadcasts to both partners.
     A received kudos (fromMemberId ≠ me) triggers confetti. */
  const [kudosOpen, setKudosOpen] = useState(false);
  const [kudosNote, setKudosNote] = useState('');
  const [kudosBusy, setKudosBusy] = useState(false);
  /* Two INDEPENDENT pieces of state (sol review M4): the burst the partner's
     kudos earns, and the small echo the sender sees on their own card. They
     used to share one seq, so an outgoing kudos was compared against a
     sequence picked from INCOMING ones — the sender never saw their echo. */
  const [celebrateSeq, setCelebrateSeq] = useState<number | null>(null);
  const [echoSeq, setEchoSeq] = useState<number | null>(null);
  /* Celebration is event-based, never history-based: the highest seq present
     when the thread has FINISHED loading is the baseline, and only a kudos
     ABOVE it counts as live (card psHEfEkK9cT2).
     The first render of a cold open has an EMPTY stream (maxSeq 0), so a
     baseline taken then treated every old kudos in the history page as new —
     the burst re-fired on every app open. Waiting for `loading === false`
     means the baseline is the real history head, and a remount with history
     already in hand also baselines correctly. */
  const kudosBaselineRef = useRef<number | null>(null);
  const kudosHandledRef = useRef<Set<number>>(new Set());
  const celebrateTimerRef = useRef<number | null>(null);
  const echoTimerRef = useRef<number | null>(null);
  /* prefers-reduced-motion gets a quiet static line instead of the particle
     layer — the JSX promised one for a long time without rendering it. */
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (composeIntent?.kind === 'kudos') { setKudosOpen(true); setKudosNote(''); onIntentConsumed?.(); }
  }, [composeIntent, onIntentConsumed]);

  /* The kudos postcard is portalled to body (card DpcUGvfaDJir), so the
     --kb-inset App writes on #app never reaches it — read the visualViewport
     here too, or the keyboard covers the panel the moment the note field
     takes focus. Same source as useKeyboardInset in App.tsx. */
  const kudosKbInset = useKeyboardInset();

  // Escape, focus and the inert background now come from the shared Overlay
  // primitive (UX item 6) — the postcard's own window listener is gone, so
  // there is one Escape contract for every surface instead of one per dialog.

  // A kudos from the partner celebrates ONCE, when it arrives (persisted
  // history never does), and its own timer is never cancelled by a re-run of
  // this effect — celebrateSeq is deliberately NOT a dependency (sol M4).
  useEffect(() => {
    if (!historyLoaded) return; // the history head is not known yet — see above
    const maxSeq = messages.reduce((n, m) => Math.max(n, m.seq), 0);
    if (kudosBaselineRef.current === null) {
      kudosBaselineRef.current = maxSeq;
      return;
    }
    const fresh = messages.filter(
      (m) => m.kind === 'kudos' && m.seq > (kudosBaselineRef.current ?? 0) && !kudosHandledRef.current.has(m.seq),
    );
    if (!fresh.length) return;
    for (const m of fresh) kudosHandledRef.current.add(m.seq);

    const theirs = fresh.filter((m) => m.fromMemberId !== null && m.fromMemberId !== myMemberId).pop();
    if (theirs) {
      setCelebrateSeq(theirs.seq);
      /* The particle layer is now app-wide (card psHEfEkK9cT2): this screen
         asks for the burst by MOMENT (the kudos seq), and the layer paints it
         unless Settings says otherwise. */
      celebrateOnce(`kudos:${theirs.seq}`);
      if (celebrateTimerRef.current !== null) window.clearTimeout(celebrateTimerRef.current);
      celebrateTimerRef.current = window.setTimeout(() => {
        setCelebrateSeq(null);
        celebrateTimerRef.current = null;
      }, 1600);
    }

    const mine = fresh.filter((m) => m.fromMemberId === myMemberId).pop();
    if (mine) {
      setEchoSeq(mine.seq);
      if (echoTimerRef.current !== null) window.clearTimeout(echoTimerRef.current);
      echoTimerRef.current = window.setTimeout(() => {
        setEchoSeq(null);
        echoTimerRef.current = null;
      }, 1600);
    }
  }, [messages, myMemberId, historyLoaded]);

  useEffect(
    () => () => {
      if (celebrateTimerRef.current !== null) window.clearTimeout(celebrateTimerRef.current);
      if (echoTimerRef.current !== null) window.clearTimeout(echoTimerRef.current);
    },
    [],
  );

  const sendKudos = async () => {
    if (kudosBusy) return;
    setKudosBusy(true);
    try {
      await api('/api/chat/kudos', { body: kudosNote.trim() ? { note: kudosNote.trim() } : {} });
      setKudosOpen(false);
      setKudosNote('');
    } catch (e) {
      console.error('[Pear] kudos failed', e);
      reportActionFailure('send the kudos');
    } finally {
      setKudosBusy(false);
    }
  };

  /* ── Decisions (card HMzNgJETH7HE) ────────────────────────────────
     A decision renders from its OWN row, not from the message text: the
     message keeps the words the author typed and carries only round_id, so
     the card draws whatever the decisions table says — sealed while pending,
     both picks once settled. The list is re-read on every `pear:decisions`
     frame (create / vote / settle), which is how the partner's card rewrites
     in place without a second message ever landing. */
  const [decisions, setDecisions] = useState<DecisionView[] | null>(null);
  const reloadDecisions = useCallback(async () => {
    try {
      const body = await listDecisions();
      setDecisions(body.decisions ?? []);
    } catch (err) {
      /* A failed read leaves the last good list standing — the thread stays
         readable and the next frame retries it. Never a blank stream. */
      console.error('[Pear] decisions read failed', err);
    }
  }, []);
  useEffect(() => {
    void reloadDecisions();
    const onDecisions = () => void reloadDecisions();
    /* Same reconnect catch-up as the requests list: a socket that re-settles
       does not re-emit the frame it missed. */
    const onReconnected = () => void reloadDecisions();
    window.addEventListener('pear:decisions', onDecisions);
    window.addEventListener('pear:reconnected', onReconnected);
    return () => {
      window.removeEventListener('pear:decisions', onDecisions);
      window.removeEventListener('pear:reconnected', onReconnected);
    };
  }, [reloadDecisions]);
  /* One card per decision, keyed by the round_id the message carries. A
     message whose round_id names nothing (a game card, or a decision the
     read has not caught up to) simply falls through to its own kind — and a
     message that has no round_id at all is still just a bubble, which is
     exactly when the arming chip is allowed to show. */
  const decisionByRound = useMemo(() => {
    const map = new Map<string, DecisionView>();
    for (const d of decisions ?? []) if (d.roundId) map.set(d.roundId, d);
    return map;
  }, [decisions]);

  /* Card es19s3sd4gJa: a tap on a decision card opens its own detail. Held
     here (not in the card) so the sheet survives the card re-rendering when a
     vote comes back mid-read, and so it resolves against the freshest list. */
  const [openDecisionId, setOpenDecisionId] = useState<string | null>(null);
  const openDecision = useMemo(
    () => (openDecisionId ? (decisions ?? []).find(d => d.decisionId === openDecisionId) ?? null : null),
    [openDecisionId, decisions],
  );

  /* Arm a typed message into a decision (child B, HMzNgJETH7HE). The chip
     hands the SERVER the message's seq, not a copy of its text: the room
     re-reads the row, refuses anyone else's message or one already claimed,
     and points messages.round_id at the decision it just made. That single
     write is what turns the bubble into the card on both phones at once —
     the client never edits its own history to make it look like a card. */
  const [armingSeq, setArmingSeq] = useState<number | null>(null);
  const armDecision = async (seq: number, text: string) => {
    if (armingSeq !== null) return;
    const intent = parseDecisionIntent(text);
    /* Re-parsed here rather than trusted from the click site: the bubble's
       words are the only source of the two options, and a message the read
       has not caught up to is simply not armable yet. */
    if (!intent) return;
    setArmingSeq(seq);
    try {
      await createDecision({
        title: intent.title,
        optionA: intent.optionA,
        optionB: intent.optionB,
        initiationSource: 'typed_message',
        sourceSeq: seq,
      });
      /* The create already broadcasts, so this read is only the author's own
         confirmation — the partner re-reads off their own frame. */
      await reloadDecisions();
    } catch (err) {
      console.error('[Pear] arm decision failed', err);
      reportActionFailure('make that a decision');
    } finally {
      setArmingSeq(null);
    }
  };

  /* Invoke-path chip (card hphiEFLMogbg). Shown only when BOTH hold:
     - the couple chose `invoke` — an `auto` couple has no chip trigger
     - at least one AI permission is on, so a tap can actually succeed
     Two small GETs on mount; the server re-checks both regardless, so a stale
     value here can only waste a tap, never produce a wrong card. */
  const [showChip, setShowChip] = useState(false);
  // Persistent concierge toggle (card DvrFWDcNA9Ix, folds 6wz9UQwCi5ND): the
  // armed state survives a send and a remount (session bag, per thread) so it
  // stays on until the user turns it off — no more one-shot disarm.
  const [armed, setArmed] = useRemembered('thread.agentArmed', false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api<{ settings: { mode: string } }>('/api/ai/agent-settings'),
          api<{ toggles: Record<string, boolean> }>('/api/ai/toggles'),
        ]);
        if (!live) return;
        const anyOn = Object.values(t.toggles ?? {}).some(Boolean);
        setShowChip(s.settings?.mode === 'invoke' && anyOn);
      } catch (err) {
        console.error('[Pear] agent chip context failed', err);
        if (live) setShowChip(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  // ---------- Filter chips ----------
  const [activeFilter, setActiveFilter] = useRemembered<'all' | 'chat' | 'asks' | 'questions'>('thread.filter', 'all');

  useEffect(() => {
    if (requestedFilter) setActiveFilter(requestedFilter);
  }, [requestedFilter]);

  /* Real filters (sol review M3): "Chat only" used to drop agent cards alone,
     so asks, questions, dates and kudos all still showed under a chip that
     promised conversation. Chat is now the couple's own words — messages and
     kudos — and every operational card is out of it; Asks and Questions are
     live rather than "coming soon" placeholders. */
  const filteredMessages = useMemo(() => {
    switch (activeFilter) {
      case 'chat':
        return messages.filter((msg) => msg.kind !== 'agent-card' && msg.kind !== 'request-card' && msg.kind !== 'question-card' && msg.kind !== 'date-card');
      case 'asks':
        return messages.filter((msg) => msg.kind === 'request-card');
      case 'questions':
        return messages.filter((msg) => msg.kind === 'question-card');
      default:
        return messages;
    }
  }, [messages, activeFilter]);

  /* Reverse chronological (card BvSZRPd9uuwW, founder email 2026-10-19):
     newest at top. Render-only — useThread keeps ascending seq because the
     `before:` paging, the kudos baseline and the echo/ack logic all read
     ascending order; flipping the ARRAY here changes what you see and
     nothing that reads history. */
  const renderMessages = useMemo(() => [...filteredMessages].reverse(), [filteredMessages]);

  // One request is ONE card in the stream: the lifecycle emits a message per
  // action, so every request-card a later one supersedes is dropped — the
  // resolution replaces the ask instead of leaving a stale Accept button
  // beside it (2eueFMxFylie).
  const supersededRequestSeqs = useMemo(() => {
    const latestByRequest = new Map<string, number>();
    for (const msg of filteredMessages) {
      if (msg.kind !== 'request-card') continue;
      const id = parseRequestCard(msg.text)?.requestId;
      if (id) latestByRequest.set(id, msg.seq);
    }
    const dead = new Set<number>();
    for (const msg of filteredMessages) {
      if (msg.kind !== 'request-card') continue;
      const id = parseRequestCard(msg.text)?.requestId;
      if (id && latestByRequest.get(id) !== msg.seq) dead.add(msg.seq);
    }
    return dead;
  }, [filteredMessages]);

  // Today's question is ONE card in the stream, not one per day. The DO keeps a
  // row per question it has ever carded (couple-room.ts syncQuestionCard) and
  // every one of them renders the label "Today's question", so the same line
  // stacked up day after day (founder report 2026-10-08). Keep the newest card
  // and drop the ones it supersedes; the Us screen's Reveal history still holds
  // the past questions, which is where they belong.
  const supersededQuestionSeqs = useMemo(() => {
    let latest = -1;
    for (const msg of filteredMessages) {
      if (msg.kind === 'question-card' && parseQuestionCard(msg.text)) latest = msg.seq;
    }
    const dead = new Set<number>();
    if (latest < 0) return dead;
    for (const msg of filteredMessages) {
      if (msg.kind === 'question-card' && msg.seq !== latest) dead.add(msg.seq);
    }
    return dead;
  }, [filteredMessages]);

  /* The "Needs attention" region used to live here (sol review item 10): a
     compact strip above the stream that re-named the cards waiting on the
     reader. Jonathan 2026-10-17 cut it — the answer card is already IN the
     stream, so the strip was the same content twice. The stream is the one
     place a waiting card waits; nothing else was moved (card G2a36nBM74sD). */

  // ---------- ComposeMenu state ----------
  const [menuOpen, setMenuOpen] = useState(false);

  /* ── Typing (card tPPB3ozS4YrK) ───────────────────────────────────────
     The indicator lives HERE, in the open thread, and nowhere else: no thread
     list row, no tab bar, no drawer preview — the card's calm constraints say
     a keystroke is a fact about one conversation, not a couple-wide status.

     Three independent gates, because each one is a different promise:
       typingOn  — the worker's PEAR_TYPING, `=== true`; a missing or false
                   config never puts the element in the DOM at all;
       from !== myMemberId — the room already relays partner-only, this is the
                   second belt in case an echo ever reaches the sender;
       the 4s timer — silence clears it, counted from ARRIVAL here, never from
                   the sender's clock. */
  const [typingFrom, setTypingFrom] = useState<string | null>(null);
  const typingTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const onTyping = (ev: Event) => {
      if (!typingOn) return;
      const from = (ev as CustomEvent<{ memberId?: unknown }>).detail?.memberId;
      if (typeof from !== 'string' || (myMemberId && from === myMemberId)) return;
      setTypingFrom(from);
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = window.setTimeout(() => {
        setTypingFrom(null);
        typingTimerRef.current = null;
      }, TYPING_TTL_MS);
    };
    window.addEventListener('pear:typing', onTyping as EventListener);
    return () => {
      window.removeEventListener('pear:typing', onTyping as EventListener);
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    };
  }, [typingOn, myMemberId]);
  // Flag switched off while a hint was showing (a config refetch, a worker
  // restart): the element goes at once rather than living out its 4s window.
  useEffect(() => {
    if (!typingOn) {
      setTypingFrom(null);
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  }, [typingOn]);

  /* ── Archive (card 2sjo3uNvaRDr) ────────────────────────────────────
     The stream is a 7-day window, so "the message from October" is not
     reachable by scrolling. This is the find affordance: a server-side
     search over the couple's whole thread, and a jump that pages history to
     the row you pick.

     Three states that must not be confused, because each is a different
     truth to the reader: no results (the word is not there), a failure
     (search could not run — onSearch resolves null), and not-supported
     (the caller passed no onSearch, which happens in the standalone probe
     mount). Collapsing failure into "no results" would tell a user their
     memory is wrong when the server was simply unreachable. */
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveQuery, setArchiveQuery] = useState('');
  const [archiveResults, setArchiveResults] = useState<ThreadMessage[] | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  /* ── Desktop rail selection (card sUIMDZCfzM0l, A1b) ──────────────
     The right context rail is a desktop-only affordance: it reads the SAME
     >=1100px three-zone token A1a caps the stream at. Selection is a small
     state here — the rail itself is a sticky overlay inside #thread-scroll,
     rendered only while a message is selected AND the desktop token is live,
     so it is never an empty box on a phone. */
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const railRef = useRef<HTMLElement | null>(null);
  const desktopMatch = useRef<MediaQueryList | null>(null);
  useEffect(() => {
    desktopMatch.current = window.matchMedia('(min-width: 1100px)');
  }, []);
  /* Esc closes the rail with the same Escape contract the shared Overlay
     uses; focus returns to the row that opened it. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedSeq !== null) {
        e.preventDefault();
        setSelectedSeq(null);
        const el = document.querySelector(`[data-msg-seq="${selectedSeq}"]`);
        if (el instanceof HTMLElement) el.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [selectedSeq]);

  const selectedMsg = useMemo(
    () => (selectedSeq !== null ? filteredMessages.find((m) => m.seq === selectedSeq) ?? null : null),
    [selectedSeq, filteredMessages],
  );

  /* Desktop-only row selection: a tap on a message row (delegated to the
     scroll container so Bubble/card components stay untouched) selects it for
     the rail. Interactive children (buttons, links, inputs) are left alone —
     the rail's own controls and the in-stream affordances must still work. */
  const onThreadRowClick = useCallback(
    (e: ReactMouseEvent) => {
      if (desktopMatch.current?.matches !== true) return;
      if (e.target instanceof Element && e.target.closest('button, a, input, textarea, select, [role="tab"], .pear-msg-save, .pear-msg-trash')) return;
      /* A kudos card is a keepsake, not a thread row (founder email
         2026-10-28): selecting it opened the row rail — "the additional
         thread on menu" — when what he wants is the card itself, standing
         on its own. It is deliberately absent from this list. */
      const row = e.target instanceof Element
        ? e.target.closest<HTMLElement>('[data-msg-seq], [data-question-card-seq], [data-request-card-seq], [data-date-card-seq], [data-date-nudge-seq]')
        : null;
      const seq = row
        ? Number(
            row.getAttribute('data-msg-seq') ??
            row.getAttribute('data-question-card-seq') ??
            row.getAttribute('data-request-card-seq') ??
            row.getAttribute('data-date-card-seq') ??
            row.getAttribute('data-date-nudge-seq'),
          )
        : NaN;
      if (Number.isFinite(seq)) {
        setSelectedSeq(seq);
        // Make the selected row focusable so Esc can return focus to it.
        row!.setAttribute('tabindex', '-1');
      }
    },
    [],
  );

  /* Opening the rail moves focus into it; closing restores the selecting row. */
  useEffect(() => {
    if (selectedSeq !== null && railRef.current) {
      const first = railRef.current.querySelector<HTMLElement>('[data-thread-rail-close], button, a, [tabindex]');
      if (first) first.focus();
    }
  }, [selectedSeq]);

  const runArchiveSearch = useCallback(async () => {
    if (!onSearch) return;
    const q = archiveQuery.trim();
    if (q.length < 2) {
      setArchiveResults(null);
      setArchiveError(null);
      return;
    }
    setArchiveBusy(true);
    setArchiveError(null);
    const found = await onSearch(q);
    setArchiveBusy(false);
    if (found === null) {
      setArchiveResults(null);
      setArchiveError('Search could not run — try again in a moment.');
      return;
    }
    setArchiveResults(found);
  }, [archiveQuery, onSearch]);

  /* Jump: page history so the target is loaded, then land on it. The scroll
     waits a beat because the history frame is a round trip — scrolling at
     once would find nothing and leave the reader at the top. If the socket
     could not take the request, say so and keep the list: a jump that
     silently did nothing is the worst of the three outcomes. */
  const jumpToResult = useCallback((seq: number) => {
    if (!onJumpToSeq) return;
    if (!onJumpToSeq(seq)) {
      setArchiveError('That message could not be loaded — reconnect and try again.');
      return;
    }
    setArchiveOpen(false);
    window.setTimeout(() => {
      const el = document.querySelector(`[data-msg-seq="${seq}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        el.setAttribute('data-archive-jumped', 'true');
        window.setTimeout(() => el.removeAttribute('data-archive-jumped'), 2400);
      }
    }, 350);
  }, [onJumpToSeq]);

  const handleComposePick = useCallback(
    (tile: ComposeTile) => {
      onComposePick?.(tile);
      if (tile === 'message') {
        // Message tile closes menu and refocuses input
        setMenuOpen(false);
        inputRef.current?.focus();
      } else {
        // Other tiles: close menu, caller handles routing
        setMenuOpen(false);
      }
    },
    [onComposePick],
  );

  const count = messages.length + echoes.length + queued.length;
  useEffect(() => {
    const el = scrollRef.current;
    // Stand aside while useScrollMemory is putting the reader back where they
    // were (UX item 5) — otherwise every mount yanks them off their place.
    // Newest-at-top (card BvSZRPd9uuwW): the new row lands at the TOP, so the
    // stream pins there instead of to the oldest message at the bottom.
    if (el && el.dataset.uiRestore !== '1') el.scrollTop = 0;
  }, [count]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    // Persistent toggle (DvrFWDcNA9Ix): the concierge invitation stays armed
    // for this and the next message until the user turns it off.
    // Card 4FFux4skKvYw: a send the socket cannot take is QUEUED, so the
    // composer stays empty — the words are on screen as a pending row, not
    // hidden back in the box waiting to be retyped. `false` is now reserved
    // for an outright rejection, which still puts the draft back.
    const handed = onSend(text, armed && showChip);
    if (handed === false) setDraft(text);
    // No disarm here: the toggle (DvrFWDcNA9Ix) stays on until the user turns
    // it off. The invitation stays armed for the next message too.
  };

  // First-move hint (Rl0vzkZ2hdAe): retire the welcome line once the couple
  // has put something in the thread. The backend writes today's question into
  // the stream as a card, so a brand-new couple is never empty — that card is
  // not "use", it is furniture, and counting it dismissed the hint seconds
  // after load. Anything else (chat, an ask, kudos, an agent card) is the
  // thread being used; a pending local echo counts too, so the line goes the
  // moment you hit Enter rather than when the server acks.
  const hasContent = echoes.length > 0 || queued.length > 0 || messages.some(m => m.kind !== 'question-card');

  // First-move hint (Rl0vzkZ2hdAe): the welcome line has done its job once
  // there is anything in the thread — remember it and stop drawing it.
  useEffect(() => {
    if (hasContent) markHintSeen('thread');
  }, [hasContent]);

  return (
    <>
      {/* Opening line, shortened (card I3nVYMBPlKHZ): one plain welcome, with
          the functional detail moved into a tooltip beside it — the wording
          Jonathan asked for was "Welcome to your thread, your starting point
          for today." Rl0vzkZ2hdAe: shown until the thread has content. */}
      <TabHint id="hint-thread" hintKey="thread">
        Welcome to your thread, your starting point for today.
        <Tooltip label="Everything you both say lives here, in order, on both phones. For example: say 'running 10 late' from the train and it is on their screen before they reach the door. The + opens Ask, Kudos and today's question; the chips below narrow what you see.">
        </Tooltip>
      </TabHint>
      {/* ---- Filter chips ----
           A real tablist (UX item 6, H8): roving tabindex so the row is one
           tab stop, arrows move between the filters, and each chip names the
           region it filters — which is now a real tabpanel (#thread-panel),
           not the scroll box the chips themselves live in: a tab may not
           control an ancestor of its own tablist. This row lives ABOVE the
           scroller, not inside it: the reader's scroll position is restored on
           mount (useScrollMemory), so a row inside the scrollport flashed once and
           then vanished behind the restored offset (founder report 2026-11-05,
           "the chips momentarily come up then disappear"). Disabled chips (the ones
           still coming) stay out of both the order and the arrow sequence. */}
      <div className="pear-chips" role="tablist" aria-label="Thread filters">
        {filterChips.map((chip) => (
          <button
            key={chip.key}
            className={[
              'pear-filter-chip',
              !chip.disabled && activeFilter === chip.key ? 'is-active' : '',
              chip.disabled ? 'is-disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            title={chip.disabled ? 'coming soon' : undefined}
            role="tab"
            id={`thread-chip-${chip.key}`}
            data-tab={chip.key}
            aria-selected={!chip.disabled && activeFilter === chip.key}
            aria-controls="thread-panel"
            tabIndex={!chip.disabled && activeFilter === chip.key ? 0 : -1}
            onKeyDown={e =>
              tabListKeyDown(e, el => {
                const next = filterChips.find(c => String(c.key) === (el.dataset.tab ?? ''));
                if (next && !next.disabled) setActiveFilter(next.key);
              })
            }
            disabled={!!chip.disabled}
            onClick={() => {
              if (!chip.disabled) setActiveFilter(chip.key);
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div id="thread-scroll" className="pear-thread" ref={scrollRef} onClick={onThreadRowClick}>
        {/* The pinned "What would you like to remember tonight?" prompt is
            gone (card 56grGaVZY5BT): it was obtrusive and its Add memory
            button was a placeholder that dismissed rather than captured.
            Capture still lives in the compose menu's Note / Idea tiles. */}

        {/* ---- Archive (card 2sjo3uNvaRDr) ----
             Sits between the chips and the stream: it is a way INTO the
             stream, not a filter of it. Closed by default so the default
             thread is unchanged — a search box permanently parked above the
             conversation is chrome the reader has to learn to ignore. */}
        {onSearch && (
          <div className="pear-archive" data-archive>
            <button
              type="button"
              className="pear-archive-toggle"
              data-archive-toggle
              aria-expanded={archiveOpen}
              aria-controls="pear-archive-panel"
              onClick={() => {
                const next = !archiveOpen;
                setArchiveOpen(next);
                if (!next) {
                  setArchiveResults(null);
                  setArchiveError(null);
                }
              }}
            >
              {archiveOpen ? 'Close archive' : 'Search the whole thread'}
            </button>

            {archiveOpen && (
              <div className="pear-archive-panel" id="pear-archive-panel">
                <form
                  className="pear-archive-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void runArchiveSearch();
                  }}
                >
                  <input
                    type="search"
                    className="pear-archive-input"
                    data-archive-input
                    value={archiveQuery}
                    /* The window is 7 days, so a word that is not in it is
                       still worth asking about — that is the whole point. */
                    placeholder="A word you remember…"
                    aria-label="Search the whole thread"
                    onChange={(e) => setArchiveQuery(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="pear-btn pear-archive-go"
                    data-archive-go
                    disabled={archiveBusy || archiveQuery.trim().length < 2}
                  >
                    {archiveBusy ? 'Searching…' : 'Search'}
                  </button>
                </form>

                {archiveError && (
                  <p className="pear-archive-error" data-archive-error role="status">{archiveError}</p>
                )}

                {!archiveError && archiveResults && archiveResults.length === 0 && (
                  <p className="pear-archive-empty" data-archive-empty role="status">
                    Nothing matches “{archiveQuery.trim()}” anywhere in your thread.
                  </p>
                )}

                {archiveResults && archiveResults.length > 0 && (
                  <>
                    <p className="pear-archive-count" data-archive-count role="status">
                      {archiveResults.length} match{archiveResults.length === 1 ? '' : 'es'}
                    </p>
                    <ul className="pear-archive-results" data-archive-results>
                      {archiveResults.map((m) => (
                        <li key={`archive-${m.seq}`}>
                          <button
                            type="button"
                            className="pear-archive-result"
                            data-archive-result
                            data-archive-seq={m.seq}
                            onClick={() => jumpToResult(m.seq)}
                          >
                            <span className="pear-archive-when">{dayLabel(m.ts)}</span>
                            {/* Cards render as their payload JSON, which is not
                                something to read in a result list — show the
                                kind instead of the envelope. The excerpt is
                                windowed on the match and the matched run is
                                marked (card vN5IhlgoFXTb): a word late in a
                                long message used to fall outside the first
                                120 characters, so the row looked unrelated to
                                what was typed. */}
                            <span className="pear-archive-excerpt">
                              {m.kind && m.kind !== 'text' ? `[${m.kind.replace(/-/g, ' ')}] ` : ''}
                              {(() => {
                                const ex = searchExcerpt(m.text, archiveQuery);
                                return (
                                  <>
                                    {ex.before}
                                    {ex.match && <mark className="pear-archive-hit">{ex.match}</mark>}
                                    {ex.after}
                                  </>
                                );
                              })()}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* The "Needs your attention" strip is gone (Jonathan 2026-10-17):
            it duplicated cards that are already in the stream — the answer
            card is right there — so a second copy above the stream was noise
            that needed its own reading pass. Card: G2a36nBM74sD. The stream
            is the single place a card waits for you. */}

        {/* ---- Life rail (FiaOoQ0mb11g) ----
            System output above the stream: chores scored to the top three,
            the rest collapsed into "Life (n)". Own block, own heading — it
            never draws as a bubble and never joins the message flow. */}
        <LifeRail nameFor={nameFor} onOpenLife={() => onNavigate?.({ section: 'life', subsection: 'chores' })} />

        {/* ---- The filtered region (UX item 6, sol round 1) ----
             The chips name THIS box, not the scroll container they sit in:
             a tab's aria-controls must resolve to a tabpanel that is not an
             ancestor of its own tablist. The life rail stays outside it —
             it is system output above the stream, not something a filter
             shows or hides. */}
        <div
          id="thread-panel"
          className="sub-view"
          role="tabpanel"
          aria-labelledby={`thread-chip-${activeFilter}`}
          /* A region you can land on: the empty thread has nothing focusable
             inside it, so without this the panel is unreachable by keyboard. */
          tabIndex={0}
        >
          {loading && !hasContent && (
            <div style={{ padding: 'var(--space-4) 0' }}>
              <SkeletonBlock lines={3} />
            </div>
          )}

          {!loading && !hasContent && (
            <div className="pear-chat-empty" id="chat-empty">
              {/* Paired vs solo: once the partner has joined there is no
                  invite left to send, so the empty thread greets the couple
                  instead of sending them back to the pairing screen. */}
              {paired ? (
                <>
                  <p className="pear-chat-empty-text">
                    Nothing here yet. Say something and the thread begins.
                  </p>
                  <p className="pear-chat-empty-hint">
                    Notes, asks and photos from both of you show up right here.
                  </p>
                </>
              ) : (
                <>
                  <p className="pear-chat-empty-text">
                    Once your partner joins with your code, your thread begins.
                  </p>
                  <p className="pear-chat-empty-hint">
                    Use the invite card above — copy the link, share it, email it, or scan the QR.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Newest at top, so the in-flight echo sits ABOVE the history —
              it is the youngest thing in the thread (card BvSZRPd9uuwW). */}
          {/* Queued sends (card 4FFux4skKvYw) sit above the in-flight echoes:
              the words the user already typed, held in the exact place the
              message will occupy, with the only path back to the network. The
              toast is the moment; THIS row is the durable record. */}
          {queued.map((q) => (
            <div
              className="pear-msg pear-msg-mine pear-local-echo pear-msg-queued"
              key={`queued-${q.clientMsgId}`}
              data-msg-queued={q.clientMsgId}
            >
              <div className="pear-msg-bubble pear-msg-bubble-mine pear-msg-echo pear-msg-echo-queued">
                {q.text}{' '}
                <span className="pear-msg-queued-note">Queued — will send when the thread reconnects.</span>
                {onRetryQueued && (
                  <button
                    type="button"
                    className="pear-msg-retry"
                    data-msg-retry={q.clientMsgId}
                    onClick={() => onRetryQueued(q.clientMsgId)}
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          ))}
          {echoes.map((echo) => (
            <div className="pear-msg pear-msg-mine pear-local-echo" key={echo.clientMsgId} data-msg-id={echo.clientMsgId}>
              <div className="pear-msg-bubble pear-msg-bubble-mine pear-msg-echo">
                {echo.text}{' '}
                <span className="pear-msg-sending">(sending…)</span>
              </div>
            </div>
          ))}

          {renderMessages.map((msg, index) => {
            const drop = supersededRequestSeqs.has(msg.seq) || supersededQuestionSeqs.has(msg.seq);
            /* Day separator (card 4ynbWszGdqUd): the stream is newest-at-top, so
               a separator belongs to the first message of each calendar day and
               is decided by comparing against the message ABOVE (index-1). A
               superseded row is dropped entirely, so it never emits a separator —
               the next visible row starts its own day instead. */
            const dayHere = dayKey(msg.ts);
            const showSep = !drop && dayHere !== null && dayHere !== dayKey(renderMessages[index - 1]?.ts);
            return drop ? null : (
              <Fragment key={`wrap-${msg.seq}`}>
                {showSep && (
                  <div className="pear-day-sep" data-day={dayHere ?? ''}>{dayLabel(msg.ts)}</div>
                )}
                {msg.roundId && decisionByRound.has(msg.roundId) ? (
                  /* A decision card (parent VcBi3nr9GjBj, child HMzNgJETH7HE).
                     The message keeps the words the author typed; the card draws
                     from the decisions row that round_id names, so pending and
                     reveal are two renders of ONE element — the partner's pick
                     arrives as a `decisions` frame and the card rewrites in
                     place. Never a second message, never a competing card. */
                  <DecisionCard
                    key={`decision-${msg.seq}`}
                    seq={msg.seq}
                    decision={decisionByRound.get(msg.roundId)!}
                    myMemberId={myMemberId}
                    nameFor={nameFor}
                    partnerName={partnerName}
                    onOpenDetail={setOpenDecisionId}
                  />
                ) : msg.kind === 'kudos' ? (
              <div className="pear-kudos-card" key={`kudos-${msg.seq}`} data-kudos-seq={msg.seq}>
                {/* One heart, not two (founder email 2026-10-28: "heart is
                    duplicated, the yellow/green should be dropped in"). The
                    mustard stamp at the card's top-right is the kept one —
                    the plain glyph that used to lead the row is gone. */}
                <div className="pear-kudos-body">
                  <span className="pear-kudos-label">
                    {msg.fromMemberId === myMemberId
                      ? 'You gave kudos'
                      // The display name is the room's own (card yFLCjm7ONIEc);
                      // 'your partner' is what is left when it has not been set.
                      : 'Kudos from ' + (nameFor?.(msg.fromMemberId) ?? partnerName ?? 'your partner')}
                  </span>
                  {msg.text && <p className="pear-kudos-text">{msg.text}</p>}
                </div>
                {/* Founder email 2026-10-28: a published kudos keeps the
                    postcard it was sent as — cream paper and a stamp corner,
                    the same object the sender held, rather than a dark card
                    that only shares its colour. */}
                <span className="pear-kudos-card-stamp" aria-hidden="true">{KUDOS_GLYPH}</span>
                {msg.fromMemberId === myMemberId && echoSeq === msg.seq && <span className="pear-kudos-echo" />}
              </div>
            ) : msg.kind === 'request-card' ? (
              /* Request lifecycle card (2eueFMxFylie): the text column carries
                 JSON {v, requestId, action, requestText}, so the parse is
                 defensive — a malformed payload degrades to a plain bubble
                 instead of throwing inside the render pass. The POSTED card
                 from the partner is the one that can be answered inline; every
                 other action is a quiet status line. */
              <RequestCard
                key={`request-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                mine={msg.fromMemberId === myMemberId}
                partnerName={nameFor?.(msg.fromMemberId) ?? 'your partner'}
                role={roleFor(msg.fromMemberId)}
              />
            ) : msg.kind === 'question-card' ? (
              /* Today's question as a typed card (jinkfGNSDI4j): one message per
                 question, rewritten in place when both have answered, so this
                 draws whatever state the payload carries. The parse is
                 defensive for the same reason the request card's is — a
                 malformed payload degrades to a plain bubble. */
              <QuestionCard
                key={`question-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                myMemberId={myMemberId}
                nameFor={nameFor}
                partnerName={partnerName}
                onOpenUs={() => onNavigate?.({ section: 'us', subsection: 'questions' })}
              />
            ) : msg.kind === 'date-card' ? (
              /* The locked-date celebration (ZdYBVP203b8a, PRD §6.3): one card,
                 posted by the app when both partners are in. A date card only
                 exists post-lock, so carrying the plan itself leaks nothing —
                 there is no pre-lock state it is ever written in. */
              <DateCard
                key={`date-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                onOpenUs={() => onNavigate?.({ section: 'us', subsection: 'dates' })}
              />
            ) : msg.kind === 'agent-card' && parseDateNudge(msg.text) ? (
              /* Cadence nudge (yejoF8uTVxyf): the concierge's one-per-month
                 prompt, couple-addressed, carrying its own deep link into the
                 surface that answers it (PRD §6.3). Concierge prose shares this
                 kind and fails the parse, so it falls through to the plain card
                 below — same defensive shape as QuestionCard/DateCard. */
              <DateNudgeCard
                key={`date-nudge-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                onOpenDates={() => onNavigate?.({ section: 'us', subsection: 'dates' })}
              />
            ) : msg.kind === 'agent-card' && msg.roundId ? (
              /* Game-round card (Bkt2clm8SSOI): same agent-card affordance as the
                 concierge, plus the link back to the panel that answers it. The
                 text is rewritten by the server on reveal, so one round is one
                 card here — never an invitation and a result side by side. */
              <div
                /* Card 6gd4aWNsDsqJ: the game card and the question card share
                   one shell (.pear-thread-card) and the same emoji slot, so the
                   two read as one family down the stream. */
                className="pear-thread-card pear-agent-card pear-game-card"
                key={`game-${msg.seq}`}
                data-msg-seq={msg.seq}
                data-game-round={msg.roundId}
              >
                <span className="pear-card-emoji" aria-hidden="true">{GAME_GLYPH}</span>
                <div className="pear-game-body">
                  <p className="pear-agent-copy">{msg.text}</p>
                  {onOpenGameRound && (
                    <button
                      type="button"
                      className="pear-btn pear-btn-mustard pear-game-open"
                      aria-label="Open Back and forth in the Us tab"
                      onClick={() => onOpenGameRound(msg.roundId!)}
                    >
                      Open in Us →
                    </button>
                  )}
                </div>
              </div>
            ) : msg.kind === 'agent-card' ? (
              <div className="pear-agent-card" key={`agent-${msg.seq}`} data-msg-seq={msg.seq}>
                <span className="pear-agent-dot" />
                <p className="pear-agent-copy">{msg.text}</p>
              </div>
            ) : msg.kind === 'system' ? (
              <div className="pear-sys-line" key={`sys-${msg.seq}`} data-msg-seq={msg.seq}>
                {msg.text}
              </div>
            ) : (
              /* An arming chip (child B, HMzNgJETH7HE) sits under the author's
                 OWN bubble and only where the words are still just words: the
                 message has no round_id, so nothing has claimed it yet. The
                 partner never sees it — a suggestion to someone else's sentence
                 is not theirs to accept. Plain messages and favour-shaped ones
                 ("could you…") parse to nothing, so no chip appears at all —
                 a favour is the Requests card's shape, not a decision's. */
              <div className="pear-decision-arming" key={`arm-${msg.seq}`}>
                <Bubble
                  seq={msg.seq}
                  text={msg.text}
                  mine={msg.fromMemberId === myMemberId}
                  role={roleFor(msg.fromMemberId)}
                  onRemoveMessage={onRemoveMessage}
                /* The sender's chosen reply ink (card 9QADJv3CdiOh) — resolved
                   off the live members list like the name below, so a Settings
                   change shows on the next message without a reload.

                   Founder email 2026-10-28: BOTH sides wear it. "partner text
                   colour" was reported as a regression, and he asked that the
                   partner also see the colour they picked — so the guard that
                   kept own messages on the role token is gone. The palette is
                   dark ink, so the cream bubble only gains contrast; the
                   mustard bubble is the measured case the list was built for. */
                colour={colourFor ? colourFor(msg.fromMemberId) : null}
                /* Label the partner's messages with their display name, once
                   per run of messages (card yFLCjm7ONIEc) — the name comes
                   from the live members list, so a Settings change shows on
                   the next message without a reload. */
                from={
                  msg.fromMemberId === myMemberId || !nameFor
                    ? null
                    : renderMessages[index - 1]?.fromMemberId === msg.fromMemberId
                      ? null
                      : nameFor(msg.fromMemberId)
                }
                />
                {/* The chip only renders for the author, on their own message,
                    and only where the words hold two options. Nothing is
                    created until it is tapped — a sentence the author left as
                    a sentence stays one. */}
                {msg.fromMemberId === myMemberId &&
                  !msg.roundId &&
                  parseDecisionIntent(msg.text) && (
                    <button
                      type="button"
                      className="pear-decision-arm-chip"
                      data-test="decision-arm-chip"
                      aria-label="Make this a decision"
                      onClick={() => void armDecision(msg.seq, msg.text)}
                    >
                      Make this a decision
                    </button>
                  )}
              </div>
            )}
              </Fragment>
            );
          })}

          {/* Windowed hydration (card 4ynbWszGdqUd): a fresh open loads only the
              last 7 days. When the server holds older messages, this affordance
              pages them in (loadOlder) — newest-at-top, so it sits at the oldest
              end of the stream, where the loaded range ends.

              Card NyA5qLuZsAgi (Jonathan 2026-11-03, "Show earlier messages is
              not visible"; reopened 2026-11-04 17:37): the control is NOT
              pinned over the stream — it sits in the flow at the end of the
              loaded range, so the reader meets it by reaching the end of the
              scroll, and a tap pages the next window in. */}
          {hasOlder && (
            <button
              type="button"
              className={`pear-thread-older${olderRevealed ? '' : ' is-unreached'}`}
              data-thread-older
              data-revealed={olderRevealed ? 'true' : 'false'}
              onClick={() => onLoadOlder?.()}
            >
              Show earlier messages
            </button>
          )}
        </div>

        {/* ---- Desktop right context rail (card sUIMDZCfzM0l, A1b) ----
            Renders ONLY when a message is selected AND the >=1100px three-zone
            token is live, so on a phone it is never in the DOM (accept line 1
            and 2). It reuses the notebook .nb-context primitive and floats in
            the right gutter the A1a ~680px cap leaves empty. */}
        {desktopMatch.current?.matches === true && selectedMsg && (
          <aside
            className="nb-context pear-thread-rail"
            data-thread-rail
            aria-label="Message details"
            ref={railRef}
            style={{ ['--pear-thread-rail-w' as string]: 'clamp(280px, 26vw, 360px)' }}
          >
            <div className="nb-context-top">
              <span className="pear-thread-rail-kicker" data-thread-rail-kicker>
                {!selectedMsg.kind || selectedMsg.kind === 'text'
                  ? selectedMsg.fromMemberId === myMemberId
                    ? 'Your message'
                    : 'Message'
                  : `Message · ${selectedMsg.kind.startsWith('request') ? 'ask' : selectedMsg.kind.replace(/-/g, ' ')}`}
              </span>
              <button
                type="button"
                className="nb-context-close"
                data-thread-rail-close
                aria-label="Close message details"
                onClick={() => setSelectedSeq(null)}
              >
                Close
              </button>
            </div>

            {/* Quick-entry tiles (card uLr0sCxQDnd7, A1c) — capture where you
                look: the shipped ComposeMenu tile defs, rendered one click from
                the rail header instead of behind the + menu. Sits AFTER the top
                row so the Close button stays the rail's first focusable element
                (A1b's focus contract). Desktop-only by construction — this whole
                aside never enters the DOM below 1100px, and the stream's pinned
                Tonight card and chrome rows are untouched. */}
            <div
              className="pear-thread-rail-quick"
              data-thread-rail-quick
              role="group"
              aria-label="Quick entry"
            >
              {TILE_DEFS.map((def) => (
                <button
                  key={def.type}
                  type="button"
                  className="pear-thread-rail-quick-tile"
                  data-thread-rail-tile={def.type}
                  title={def.label}
                  onClick={() => handleComposePick(def.type)}
                >
                  <span className="pear-thread-rail-quick-icon" aria-hidden="true">
                    {def.icon}
                  </span>
                  <span className="pear-thread-rail-quick-label">{def.label}</span>
                </button>
              ))}
            </div>

            <div className="pear-thread-rail-msg" data-thread-rail-msg>
              <div className="pear-msg-bubble" data-thread-rail-text>
                {selectedMsg.kind === 'text' ? selectedMsg.text : selectedMsg.text}
              </div>
              {selectedMsg.ts && (
                <p className="nb-context-meta" data-thread-rail-when>
                  {dayLabel(selectedMsg.ts)} · {new Date(selectedMsg.ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
              <div className="pear-thread-rail-actions" data-thread-rail-actions>
                {/* Save to Notebook — the same route the in-stream affordance
                    hits; rendered here so the rail owns the action. */}
                <MessageSave seq={selectedMsg.seq} text={selectedMsg.text} />
                {/* Only self-authored text rows are removable (the DO enforces
                    it); the rail mirrors the trash affordance. */}
                {selectedMsg.kind === 'text' &&
                  selectedMsg.fromMemberId === myMemberId &&
                  onRemoveMessage && (
                    <button
                      type="button"
                      className="pear-thread-rail-action"
                      data-thread-rail-delete
                      onClick={() => {
                        if (window.confirm('Delete this message for you both?')) {
                          onRemoveMessage(selectedMsg.seq);
                          setSelectedSeq(null);
                        }
                      }}
                    >
                      Delete
                    </button>
                  )}
                {/* Card links — the "Open in Us →" family, one tap from the
                    rail instead of a modal/navigation the stream forced. */}
                {selectedMsg.kind === 'request-card' && onNavigate && (
                  <button type="button" className="pear-thread-rail-action" data-thread-rail-link
                    onClick={() => onNavigate({ section: 'life', subsection: 'requests' })}>
                    Open Requests
                  </button>
                )}
                {selectedMsg.kind === 'question-card' && onNavigate && (
                  <button type="button" className="pear-thread-rail-action" data-thread-rail-link
                    onClick={() => onNavigate({ section: 'us', subsection: 'questions' })}>
                    Open Questions
                  </button>
                )}
                {selectedMsg.kind === 'date-card' && onNavigate && (
                  <button type="button" className="pear-thread-rail-action" data-thread-rail-link
                    onClick={() => onNavigate({ section: 'us', subsection: 'dates' })}>
                    Open Dates
                  </button>
                )}
              </div>
            </div>
          </aside>
        )}

      </div>


      {/* ---- Composer, BOTTOM-docked (card CwbqiUPwRg02, reverses
          dZjN98QXAgEH) ----
          Jonathan 2026-10-17: the entry box "should sit at the BOTTOM,
          be bigger, multiline" — a chat composer belongs where you type,
          under the conversation, not above it. The block renders after
          #thread-scroll, so .pear-chat's column flex seats it at the foot
          of the frame (above the tab bar, which .pear-app's bottom padding
          already clears). Its position:relative is still the containing
          block for ComposeMenu's absolute overlay — which now opens
          UPWARD from this bottom anchor. */}
      <div className="pear-chat-input-area">
        {/* ComposeMenu anchors to this block and opens UPWARD now (the
            anchor sits at the bottom of the frame, card CwbqiUPwRg02) */}
        <ComposeMenu open={menuOpen} onClose={() => setMenuOpen(false)} onPick={handleComposePick} />

        {/* Kudos sender (card V0dtVf36Dplz) — compose-menu deep-link target.
            Card DpcUGvfaDJir: the old in-flow sender floated above the
            composer inside `.pear-chat-input-area`, so on a phone it was
            squeezed into whatever height the thread had left and clipped —
            and it read as a form row, not a gesture. It is a postcard now:
            a backdrop over the whole app and one paper panel portalled to
            body, so no transformed ancestor can trap the overlay and no short
            thread can crop it. Backdrop tap, outside pointer and Escape all
            cancel; focus and the inert background are the shared Overlay's
            contract (UX item 6), not this screen's. */}
        <Overlay
          open={kudosOpen}
          onClose={() => setKudosOpen(false)}
          className="pear-kudos-overlay"
          testId="kudos-overlay"
          style={{ ['--kb-inset' as string]: `${kudosKbInset}px` }}
          panelClassName="pear-kudos-sender"
          label="Give kudos"
          /* The postcard opens FROM the compose menu, which unmounts as it
             opens — so the remembered opener is gone by the time focus has to
             come back. Hand it the + button explicitly. */
          restoreFocusTo=".pear-compose-toggle"
        >
          {/* Stamp corner — the gesture's mark on the card, top-right
              where a postcard stamps. Decorative: the glyph is already
              announced by the dialog's label. */}
          <span className="pear-kudos-stamp" aria-hidden="true">
            <span className="pear-kudos-sender-glyph">{KUDOS_GLYPH}</span>
          </span>

          <p className="pear-kudos-to">For {partnerName ?? 'your partner'}</p>

          <textarea
            className="pear-chat-input pear-kudos-note"
            placeholder="That was lovely — say why (optional)"
            aria-label="Kudos note"
            value={kudosNote}
            autoFocus
            rows={3}
            maxLength={200}
            onChange={e => setKudosNote(e.target.value)}
          />

          <div className="pear-kudos-actions">
            <button
              type="button"
              className="pear-btn"
              aria-label="Cancel kudos"
              onClick={() => setKudosOpen(false)}
            >
              Cancel
            </button>
            {/* The panel is a div now, not a <form> — the Overlay owns the
                panel element, so submission is the button's own handler
                rather than an implicit form submit. */}
            <button
              type="button"
              className="pear-btn pear-btn-mustard"
              disabled={kudosBusy}
              aria-label="Send kudos"
              onClick={() => void sendKudos()}
            >
              {kudosBusy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </Overlay>

        {/* H7 (card ESidDNa3mmaP) — the visible half of a failed action:
            one bottom alert, portalled to body like the update toast. */}
        <ActionErrorToast />

        {/* Received kudos: the app-wide confetti layer paints the burst (card
            psHEfEkK9cT2), so this screen keeps only the announcement and the
            reduced-motion line. Both are announced politely; the particle
            layer stays decorative and aria-hidden. */}
        <p className="pear-sr-only" role="status" aria-live="polite">
          {celebrateSeq !== null ? `Kudos from ${partnerName ?? 'your partner'}` : ''}
        </p>
        {celebrateSeq !== null && reduceMotion && (
          <div className="pear-kudos-toast" data-kudos-toast={celebrateSeq}>
            {KUDOS_GLYPH} Kudos from {partnerName ?? 'your partner'}
          </div>
        )}

        {/* Card tPPB3ozS4YrK — the partner's keystrokes, in the open thread
            and nowhere else. Announced politely for assistive tech, never
            paired with a delivery or seen tick, and gone on its own after
            TYPING_TTL_MS of silence: it says "right now", never "they read
            it". data-typing-indicator carries the sender's member id so the
            walk can prove the element appears, clears, and never renders when
            the flag is off. */}
        {typingOn && typingFrom && (
          <p className="pear-typing" data-typing-indicator={typingFrom} role="status" aria-live="polite">
            <span className="pear-typing-dots" aria-hidden="true">
              <i /><i /><i />
            </span>
            {nameFor?.(typingFrom) ?? 'Your partner'} is typing…
          </p>
        )}

        {/* Two rows (founder 2026-10-17): the box takes a whole row so it can
            be full width and grow with the draft, and the controls — the +
            toggle, the concierge switch and send — sit under it instead of
            squeezing the field to ~65% of the composer. Shared composer
            surface (card 4DrBwftybJ7q) so the question panel grows the same
            way. */}
        <Composer
          id="chat-input"
          formClassName="pear-composer"
          formId="chat-form"
          inputClassName="pear-chat-input pear-composer-input"
          ariaLabel="Message input"
          placeholder="Type a reply…"
          value={draft}
          maxLength={THREAD_TEXT_MAX}
          onChange={(next) => {
            setDraft(next);
            // Card tPPB3ozS4YrK — a hint only when there is something to hint
            // at. useThread collapses a burst of keystrokes to one frame per
            // 2s, and an empty (or whitespace-only) draft sends nothing at all:
            // backspacing to nothing must not leave the partner watching a
            // sentence that is no longer being written.
            if (typingOn && next.trim().length > 0) onTyping?.();
          }}
          onSubmit={submit}
          textAreaRef={inputRef}
          sendAriaLabel="Send message"
          sendLabel="→"
          sendClassName="pear-btn pear-btn-mustard pear-send"
          controls={
            <>
              <button
                type="button"
                className="pear-btn pear-btn-mustard pear-compose-toggle"
                aria-label="Composing options"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                +
              </button>
              {showChip && (
                /* Card yNo09LssaaFf + DvrFWDcNA9Ix: reads as a SWITCH, not a
                   text pill button — a small track + thumb that slides
                   (aria-pressed carries the state, so walk-agent-chip keeps
                   working). The armed state is PERSISTENT now: it survives a
                   send and a remount, so one tap arms every message until the
                   user taps it off. (Folds 6wz9UQwCi5ND — same toggle,
                   persists per thread.) The track is 34px; the button box is
                   padded to a 44px hit area. */
                <button
                  type="button"
                  id="agent-chip"
                  className={`pear-chip-agent${armed ? ' is-on' : ''}`}
                  aria-pressed={armed}
                  aria-label="Ask the concierge to weigh in on this message"
                  title="Ask the concierge"
                  onClick={() => setArmed(!armed)}
                >
                  {/* Founder email 2026-10-19: sparkles replaces the bulb as the
                      AI glyph (lucide sparkles, inlined — no icon dep). */}
                  <span className="pear-chip-agent-spark" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                      <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
                    </svg>
                  </span>
                  <span className="pear-chip-agent-track" aria-hidden="true">
                    <span className="pear-chip-agent-thumb" />
                  </span>
                  {/* Founder email 2026-10-23: the word rides ONLY with the
                      switch on — off, the sparkle + track carry the control
                      and the caption stays out of the row. aria-pressed and
                      the button's name are unchanged, so assistive tech and
                      the walks still read the state. */}
                  {armed && <span className="pear-chip-agent-label">Concierge</span>}
                  {/* Card cyNoIpzWN3sd (Jonathan 2026-10-20): the on/off WORDS
                      are gone. The track and thumb already show which way the
                      switch is set, so spelling the state out beside the glyph
                      was clutter on the one row that has no air to spare.
                      `aria-pressed` still carries the state for assistive
                      tech, and the accessible name/tooltip are unchanged. */}
                </button>
              )}
            </>
          }
        />
      </div>

      {/* Decision detail (card es19s3sd4gJa): the card's full entry. Rendered
          after the composer so it sits over the whole frame; the thread below
          keeps its scroll, so closing returns to the same place. */}
      {openDecision && (
        <DecisionDetail
          decision={openDecision}
          myMemberId={myMemberId}
          nameFor={nameFor}
          partnerName={partnerName}
          onClose={() => setOpenDecisionId(null)}
        />
      )}
    </>
  );
}
// --- Helper array for filter chips ---
const filterChips = [
  { key: 'all' as const, label: 'All', disabled: false },
  { key: 'chat' as const, label: 'Chat', disabled: false },
  /* Live as of sol item 10 — they are filters, not promises. */
  { key: 'asks' as const, label: 'Asks', disabled: false },
  { key: 'questions' as const, label: 'Questions', disabled: false },
];
